import { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Heading,
  Button,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";

import conversationImage from "../assets/conversation.jpg";
import callCenterImage from "../assets/call-center.jpg";
import leadsImage from "../assets/leads.jpg";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);

const slides = [
  {
    tag: "For Real Estate Agents",
    headline: "Stop Cold Calling.",
    headlineAccent: "Start Closing Deals.",
    body: "We are the professional voice that connects you with motivated landlords and property owners. Focus on showings — we'll handle the calls.",
    cta1: { label: "Partner With Us", action: "contact" },
    cta2: { label: "Get a Quote", action: "modal" },
    // Professional woman with headset at modern workstation
    image: callCenterImage,
    imagePosition: "center 30%",
  },
  {
    tag: "For Landlords",
    headline: "Tired of Vetting",
    headlineAccent: "Unqualified Tenants?",
    body: "Let our professional communication team match you with trusted real estate agents and qualified prospects. Hassle-free property management starts here.",
    cta1: { label: "Find an Agent", action: "modal" },
    cta2: { label: "List Your Property", action: "modal" },
    // Modern professional office team collaboration
    image: leadsImage,
    imagePosition: "center 40%",
  },
  {
    tag: "Technology & Reach",
    headline: "Meaningful Conversations",
    headlineAccent: "at Scale.",
    body: "Strategic outreach designed to create new property opportunities. We are the bridge between opportunity and execution.",
    cta1: { label: "Our Process", action: "about" },
    cta2: { label: "Start Today", action: "modal" },
    // Laptop + headset on clean desk — CRM/tech feel
    image: conversationImage,
    imagePosition: "center 50%",
  },
];

export default function HeroSection({ onOpenModal }) {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);

  const startTimer = () => {
    setProgress(0);
    let p = 0;
    intervalRef.current = setInterval(() => {
      p += 1;
      setProgress(p);
      if (p >= 100) {
        setCurrent((c) => (c + 1) % slides.length);
        p = 0;
        setProgress(0);
      }
    }, 60);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(intervalRef.current);
  }, [current]);

  const goTo = (i) => {
    clearInterval(intervalRef.current);
    setCurrent(i);
  };

  const handleCTA = (action) => {
    if (action === "modal") {
      onOpenModal();
    } else {
      document.getElementById(action)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const slide = slides[current];

  return (
    <Box
      id="home"
      position="relative"
      h="100vh"
      minH="700px"
      overflow="hidden"
      bg="brand.black"
    >
      {/* ── LAYER 0: Background image (per slide, animated) ── */}
      <AnimatePresence>
        <MotionBox
          key={`bg-${current}`}
          position="absolute"
          inset="0"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          backgroundImage={`url(${slides[current].image})`}
          backgroundSize="cover"
          backgroundPosition={slides[current].imagePosition}
          pointerEvents="none"
        />
      </AnimatePresence>

      {/* ── LAYER 1: Dark gradient overlay — left heavy for text readability ── */}
      <Box
        position="absolute"
        inset="0"
        background="linear-gradient(100deg, rgba(13,13,13,0.93) 0%, rgba(13,13,13,0.82) 45%, rgba(13,13,13,0.38) 75%, rgba(13,13,13,0.15) 100%)"
        pointerEvents="none"
        zIndex={1}
      />

      {/* ── LAYER 2: Coral vignette at bottom ── */}
      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        h="35%"
        background="linear-gradient(to top, rgba(13,13,13,0.9) 0%, transparent 100%)"
        pointerEvents="none"
        zIndex={2}
      />

      {/* ── LAYER 3: Subtle grid texture ── */}
      <Box
        position="absolute"
        inset="0"
        opacity="0.025"
        backgroundImage="linear-gradient(#E05C3A 1px, transparent 1px), linear-gradient(90deg, #E05C3A 1px, transparent 1px)"
        backgroundSize="80px 80px"
        pointerEvents="none"
        zIndex={3}
      />

      {/* ── LAYER 4: Geometric decorative shapes (on top of image) ── */}

      {/* Outer spinning ring */}
      <Box
        position="absolute"
        top="12%"
        right={{ base: "2%", lg: "5%" }}
        w={{ base: "240px", md: "340px", lg: "480px" }}
        h={{ base: "240px", md: "340px", lg: "480px" }}
        borderRadius="50%"
        border="1px solid rgba(224,92,58,0.18)"
        animation="slowSpin 28s linear infinite"
        pointerEvents="none"
        zIndex={4}
      />

      {/* Inner static ring */}
      <Box
        position="absolute"
        top="20%"
        right={{ base: "8%", lg: "12%" }}
        w={{ base: "140px", md: "210px", lg: "300px" }}
        h={{ base: "140px", md: "210px", lg: "300px" }}
        borderRadius="50%"
        border="1px solid rgba(224,92,58,0.28)"
        pointerEvents="none"
        zIndex={4}
      />

      {/* Innermost filled glow ring */}
      <Box
        position="absolute"
        top="28%"
        right={{ base: "13%", lg: "19%" }}
        w={{ base: "80px", md: "120px", lg: "160px" }}
        h={{ base: "80px", md: "120px", lg: "160px" }}
        borderRadius="50%"
        background="radial-gradient(circle, rgba(224,92,58,0.22) 0%, transparent 70%)"
        border="1px solid rgba(224,92,58,0.35)"
        pointerEvents="none"
        zIndex={4}
      />

      {/* Top-right corner bracket */}
      <Box
        position="absolute"
        top={{ base: "90px", md: "100px" }}
        right={{ base: "16px", md: "40px" }}
        w={{ base: "50px", md: "70px" }}
        h={{ base: "50px", md: "70px" }}
        borderTop="1.5px solid rgba(224,92,58,0.5)"
        borderRight="1.5px solid rgba(224,92,58,0.5)"
        pointerEvents="none"
        zIndex={4}
      />

      {/* Bottom-left accent line */}
      <Box
        position="absolute"
        bottom={{ base: "90px", md: "110px" }}
        left={{ base: "16px", md: "40px" }}
        w={{ base: "50px", md: "70px" }}
        h={{ base: "50px", md: "70px" }}
        borderBottom="1.5px solid rgba(224,92,58,0.35)"
        borderLeft="1.5px solid rgba(224,92,58,0.35)"
        pointerEvents="none"
        zIndex={4}
      />

      {/* Diagonal slash decorative line */}
      <Box
        position="absolute"
        top="55%"
        right={{ base: "5%", lg: "8%" }}
        w={{ base: "80px", lg: "120px" }}
        h="1px"
        bg="rgba(224,92,58,0.3)"
        transform="rotate(-45deg)"
        pointerEvents="none"
        zIndex={4}
      />

      {/* Bottom sage glow */}
      <Box
        position="absolute"
        bottom="-15%"
        left="-5%"
        w={{ base: "300px", lg: "500px" }}
        h={{ base: "300px", lg: "500px" }}
        borderRadius="50%"
        background="radial-gradient(circle, rgba(89,92,75,0.18) 0%, transparent 70%)"
        pointerEvents="none"
        zIndex={4}
      />

      {/* ── LAYER 5: Slide text content ── */}
      <AnimatePresence mode="wait">
        <MotionFlex
          key={current}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          position="absolute"
          inset="0"
          align="center"
          px={{ base: 6, md: 12, lg: 20 }}
          pt="80px"
          zIndex={5}
        >
          <Box maxW="1400px" mx="auto" w="full">
            <Box maxW={{ base: "100%", lg: "62%" }}>
              {/* Tag line */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <HStack mb={6} spacing={3}>
                  <Box w="32px" h="1px" bg="brand.coral" flexShrink={0} />
                  <Text
                    fontSize="xs"
                    letterSpacing="0.3em"
                    textTransform="uppercase"
                    color="brand.coral"
                    fontWeight="600"
                  >
                    {slide.tag}
                  </Text>
                </HStack>
              </MotionBox>

              {/* Headlines */}
              <MotionHeading
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                fontSize={{ base: "4xl", md: "6xl", lg: "7xl", xl: "8xl" }}
                fontFamily="'Playfair Display', serif"
                fontWeight="400"
                lineHeight="1.0"
                mb={1}
                color="brand.cream"
              >
                {slide.headline}
              </MotionHeading>

              <MotionHeading
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                fontSize={{ base: "4xl", md: "6xl", lg: "7xl", xl: "8xl" }}
                fontFamily="'Playfair Display', serif"
                fontStyle="italic"
                fontWeight="700"
                lineHeight="1.0"
                mb={8}
                color="brand.coral"
              >
                {slide.headlineAccent}
              </MotionHeading>

              {/* Body */}
              <MotionText
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                fontSize={{ base: "md", md: "lg" }}
                color="rgba(245,240,235,0.65)"
                lineHeight="1.9"
                maxW="480px"
                mb={10}
              >
                {slide.body}
              </MotionText>

              {/* CTAs */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <HStack spacing={4} wrap="wrap">
                  <Button
                    variant="coral"
                    onClick={() => handleCTA(slide.cta1.action)}
                  >
                    {slide.cta1.label}
                  </Button>
                  <Button
                    variant="outlineLight"
                    onClick={() => handleCTA(slide.cta2.action)}
                  >
                    {slide.cta2.label}
                  </Button>
                </HStack>
              </MotionBox>
            </Box>
          </Box>
        </MotionFlex>
      </AnimatePresence>

      {/* ── LAYER 5: Slide Progress Indicators ── */}
      <Box
        position="absolute"
        bottom={{ base: "30px", md: "48px" }}
        left={{ base: "24px", md: "80px", lg: "160px" }}
        zIndex={5}
      >
        <HStack spacing={4} mb={3}>
          {slides.map((_, i) => (
            <Box
              key={i}
              cursor="pointer"
              onClick={() => goTo(i)}
              position="relative"
              h="2px"
              w={i === current ? "40px" : "20px"}
              bg={
                i === current
                  ? "brand.coral"
                  : "rgba(245,240,235,0.2)"
              }
              transition="all 0.4s ease"
              overflow="hidden"
            >
              {i === current && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  h="full"
                  bg="brand.coralLight"
                  style={{ width: `${progress}%` }}
                  transition="width 0.06s linear"
                />
              )}
            </Box>
          ))}
        </HStack>
        <Text fontSize="xs" color="brand.muted" letterSpacing="0.2em">
          {String(current + 1).padStart(2, "0")} /{" "}
          {String(slides.length).padStart(2, "0")}
        </Text>
      </Box>

      {/* Scroll Indicator */}
      <Box
        position="absolute"
        bottom={{ base: "30px", md: "48px" }}
        right={{ base: "24px", md: "80px" }}
        cursor="pointer"
        zIndex={5}
        onClick={() =>
          document.getElementById("difference")?.scrollIntoView({ behavior: "smooth" })
        }
      >
        <VStack spacing={2}>
          <Text
            fontSize="9px"
            letterSpacing="0.3em"
            color="brand.muted"
            textTransform="uppercase"
            sx={{ writingMode: "vertical-rl" }}
          >
            Scroll
          </Text>
          <Box
            w="1px"
            h="60px"
            bg="rgba(224,92,58,0.25)"
            position="relative"
            overflow="hidden"
          >
            <MotionBox
              position="absolute"
              left="0"
              w="full"
              h="40%"
              bg="brand.coral"
              animate={{ top: ["-40%", "140%"] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </Box>
        </VStack>
      </Box>

      <style>{`
        @keyframes slowSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
}
